import React, { useState } from 'react';
import { Share2, Copy, Check, MessageCircle, Linkedin, X } from 'lucide-react';
import { toast } from 'sonner';
import { getFrontendUrl } from '../utils/urlConfig';

const ShareButtons = ({ 
  title, 
  description, 
  url, 
  shareText,
  type = "job" // job, challenge, profile
}) => {
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Always anchor share URLs to the public EvalentX domain (never preview/internal)
  const fullUrl = url.startsWith('http') ? url : `${getFrontendUrl()}${url}`;
  
  const shareData = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${shareText || title}\n\n${fullUrl}`)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullUrl)}`,
  };
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };
  
  const handleShare = (platform) => {
    window.open(shareData[platform], '_blank', 'width=600,height=400');
  };
  
  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors text-sm"
        data-testid="share-btn"
      >
        <Share2 size={16} />
        Share
      </button>
      
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0a0a0a] rounded-2xl max-w-md w-full border border-white/10">
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Share2 size={20} className="text-blue-400" />
                  Share {type === 'job' ? 'Job' : type === 'challenge' ? 'Challenge' : 'Profile'}
                </h3>
                <button 
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X size={20} />
                </button>
              </div>
              <p className="text-gray-400 text-sm mt-1 line-clamp-2">{title}</p>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Share Platforms */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleShare('whatsapp')}
                  className="flex items-center justify-center gap-2 p-3 bg-green-500/20 text-green-400 rounded-xl hover:bg-green-500/30 transition-colors"
                  data-testid="share-whatsapp"
                >
                  <MessageCircle size={20} />
                  WhatsApp
                </button>
                <button
                  onClick={() => handleShare('linkedin')}
                  className="flex items-center justify-center gap-2 p-3 bg-blue-500/20 text-blue-400 rounded-xl hover:bg-blue-500/30 transition-colors"
                  data-testid="share-linkedin"
                >
                  <Linkedin size={20} />
                  LinkedIn
                </button>
              </div>
              
              {/* Copy Link */}
              <div className="flex items-center gap-2 p-3 bg-white/5 rounded-xl border border-white/10">
                <input
                  type="text"
                  value={fullUrl}
                  readOnly
                  className="flex-1 bg-transparent text-sm text-gray-300 outline-none truncate"
                />
                <button
                  onClick={handleCopyLink}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    copied 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                  data-testid="copy-link"
                >
                  {copied ? (
                    <><Check size={14} /> Copied</>
                  ) : (
                    <><Copy size={14} /> Copy</>
                  )}
                </button>
              </div>
              
              {/* Share Preview */}
              <div className="p-4 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/20">
                <p className="text-xs text-gray-400 mb-2">Share Preview:</p>
                <p className="text-sm text-white">{shareText || title}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareButtons;
